/* eslint-disable react-hooks/exhaustive-deps */
import { AddIcon } from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Box,
  Button,
  Heading,
  Tag,
} from "@chakra-ui/react";
import { nanoid } from "nanoid";
import { forwardRef } from "react";
import { FormProvider } from "react-hook-form";

/* ================= DND ================= */
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

/* ================= LOCAL ================= */
import { useRulesForm } from "../../utils/RulesEditing/useRulesForm";
import RuleItem from "./Rules Editing/RuleItem";
import SortableAccordionItem from "./Rules Editing/SortableAccordionItem";

const RulesEditor = forwardRef(({ schema }, ref) => {
  const { methods, fields, append, remove, move } = useRulesForm(schema, ref);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((r) => r.id === active.id);
    const newIndex = fields.findIndex((r) => r.id === over.id);

    move(oldIndex, newIndex);
  };

  return (
    <FormProvider {...methods}>
      <Box
        p={4}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        bg="gray.50"
      >
        {/* HEADER */}
        <Box
          position="sticky"
          top={0}
          bg="white"
          p={3}
          mb={4}
          borderBottom="1px solid"
          borderColor="gray.200"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          shadow="sm"
          zIndex={1}
        >
          <Heading size="md" color="blue.700">
            ⚙️ Rules Editor
          </Heading>

          <Button
            colorScheme="teal"
            leftIcon={<AddIcon />}
            onClick={() =>
              append({
                id: nanoid(),
                TYPE: "RULE",
                ID: `NEW_${Date.now()}`,
                TRUE_STEP: [],
                FALSE_STEP: [],
              })
            }
          >
            Add Rule
          </Button>
        </Box>

        {/* DRAG + LIST */}
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fields.map((r) => r.id)}
            strategy={verticalListSortingStrategy}
          >
            <Accordion allowMultiple reduceMotion>
              <Box
                sx={{
                  columnCount: 2,
                  columnGap: "10px",
                  "& > *": {
                    breakInside: "avoid",
                    marginBottom: "02px",
                  },
                }}
              >
                {fields.map((rule, i) => (
                  <SortableAccordionItem key={rule.id} id={rule.id}>
                    {({ attributes, listeners }) => (
                      <Box
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        bg="white"
                        shadow="xs"
                      >
                        <AccordionButton
                          _expanded={{ bg: "blue.50" }}
                          px={4}
                          py={3}
                        >
                          {/* DRAG HANDLE */}
                          <Box
                            {...attributes}
                            {...listeners}
                            mr={3}
                            cursor="grab"
                            userSelect="none"
                          >
                            ☰
                          </Box>

                          <Box flex="1" textAlign="left" fontWeight="semibold">
                            {rule.ID || `Rule ${i + 1}`}
                            <Tag ml={2} size="sm" colorScheme="blue">
                              {rule.TYPE}
                            </Tag>
                          </Box>
                          <Tag borderRadius="full" colorScheme="teal" mr={5}>
                            {i + 1}
                          </Tag>

                          <AccordionIcon />
                        </AccordionButton>

                        <AccordionPanel px={0} pt={3}>
                          <RuleItem
                            control={methods.control}
                            register={methods.register}
                            schema={schema}
                            rule={rule}
                            path={`rules.${i}`}
                            remove={() => remove(i)}
                            depth={0}
                            isStep={false}
                          />
                        </AccordionPanel>
                      </Box>
                    )}
                  </SortableAccordionItem>
                ))}
              </Box>
            </Accordion>
          </SortableContext>
        </DndContext>
      </Box>
    </FormProvider>
  );
});

export default RulesEditor;
